import { useForm, useFieldArray, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { X, Plus, Trash2, AlertTriangle, Euro, Calendar, Building2, CreditCard } from 'lucide-react'
import { FormField } from '@/components/serge/FormField'
import {
  paymentScheduleFormSchema,
  defaultPaymentScheduleValues,
  paymentTermsLabels,
  paymentMethodLabels,
  type PaymentScheduleFormData,
} from '@/lib/paymentScheduleFormSchema'

interface PaymentScheduleFormModalProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (data: PaymentScheduleFormData) => void
  isSubmitting?: boolean
  projectName?: string
}

export function PaymentScheduleFormModal({
  isOpen,
  onClose,
  onSubmit,
  isSubmitting = false,
  projectName,
}: PaymentScheduleFormModalProps) {
  const {
    register,
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: zodResolver(paymentScheduleFormSchema) as any,
    defaultValues: defaultPaymentScheduleValues,
  })

  const {
    fields: milestoneFields,
    append: appendMilestone,
    remove: removeMilestone,
  } = useFieldArray({
    control,
    name: 'milestones',
  })

  const paymentTerms = watch('paymentTerms')
  const milestones = watch('milestones') as PaymentScheduleFormData['milestones'] | undefined
  const totalPercentage = milestones?.reduce((sum, m) => sum + (m.percentage || 0), 0) || 0

  if (!isOpen) return null

  const handleFormSubmit = (data: unknown) => {
    onSubmit(data as PaymentScheduleFormData)
  }

  // Helper pour obtenir les erreurs de champs imbriqués
  const getMilestoneError = (index: number, field: string) => {
    const milestoneErrors = errors.milestones as Record<number, Record<string, { message?: string }>> | undefined
    return milestoneErrors?.[index]?.[field] as { message?: string } | undefined
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80">
      <div className="border border-gray-700 bg-gray-900 w-full max-w-4xl max-h-[90vh] flex flex-col shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-700">
          <div>
            <h2 className="text-lg font-semibold text-white">
              Configuration de l'échéancier de paiement
            </h2>
            {projectName && (
              <p className="text-sm text-gray-400 mt-1">Projet: {projectName}</p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-white transition"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form Content */}
        <form onSubmit={handleSubmit(handleFormSubmit)} className="flex-1 overflow-y-auto">
          <div className="p-6 space-y-8">
            {/* Section 1: Informations financières */}
            <section className="space-y-4">
              <div className="flex items-center gap-2 text-blue-400">
                <Euro className="w-5 h-5" />
                <h3 className="text-sm font-mono uppercase tracking-widest">
                  Informations financières
                </h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <FormField label="Montant total HT" required error={errors.totalAmount as { message?: string }}>
                  <div className="relative">
                    <input
                      type="number"
                      {...register('totalAmount', { valueAsNumber: true })}
                      className="w-full border border-gray-700 bg-gray-800 p-2 pr-10 text-sm text-white font-mono focus:outline-none focus:border-blue-500 transition"
                      placeholder="10000"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">
                      €
                    </span>
                  </div>
                </FormField>

                <FormField label="Taux de TVA" error={errors.tvaRate as { message?: string }}>
                  <div className="relative">
                    <input
                      type="number"
                      {...register('tvaRate', { valueAsNumber: true })}
                      className="w-full border border-gray-700 bg-gray-800 p-2 pr-10 text-sm text-white font-mono focus:outline-none focus:border-blue-500 transition"
                      placeholder="20"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">
                      %
                    </span>
                  </div>
                </FormField>

                <FormField label="Devise" error={errors.currency as { message?: string }}>
                  <select
                    {...register('currency')}
                    className="w-full border border-gray-700 bg-gray-800 p-2 text-sm text-white focus:outline-none focus:border-blue-500 transition"
                  >
                    <option value="EUR">EUR (€)</option>
                    <option value="USD">USD ($)</option>
                    <option value="GBP">GBP (£)</option>
                  </select>
                </FormField>
              </div>
            </section>

            {/* Section 2: Conditions de paiement */}
            <section className="space-y-4">
              <div className="flex items-center gap-2 text-blue-400">
                <Calendar className="w-5 h-5" />
                <h3 className="text-sm font-mono uppercase tracking-widest">
                  Conditions de paiement
                </h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  label="Délai de paiement"
                  required
                  error={errors.paymentTerms as { message?: string }}
                  tooltip="Conformément au Code de commerce (L.441-10)"
                >
                  <select
                    {...register('paymentTerms')}
                    className="w-full border border-gray-700 bg-gray-800 p-2 text-sm text-white focus:outline-none focus:border-blue-500 transition"
                  >
                    {Object.entries(paymentTermsLabels).map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </FormField>

                {paymentTerms === 'custom' && (
                  <FormField label="Nombre de jours" required error={errors.customPaymentDays as { message?: string }}>
                    <input
                      type="number"
                      {...register('customPaymentDays', { valueAsNumber: true })}
                      className="w-full border border-gray-700 bg-gray-800 p-2 text-sm text-white font-mono focus:outline-none focus:border-blue-500 transition"
                      placeholder="45"
                      min="1"
                      max="60"
                    />
                  </FormField>
                )}

                <FormField
                  label="Taux de pénalité de retard"
                  error={errors.latePenaltyRate as { message?: string }}
                  tooltip="Taux BCE + 10 points = 12.15% (2026)"
                >
                  <div className="relative">
                    <input
                      type="number"
                      step="0.01"
                      {...register('latePenaltyRate', { valueAsNumber: true })}
                      className="w-full border border-gray-700 bg-gray-800 p-2 pr-10 text-sm text-white font-mono focus:outline-none focus:border-blue-500 transition"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">
                      %
                    </span>
                  </div>
                </FormField>
              </div>

              <FormField
                label="Modes de paiement acceptés"
                required
                error={errors.paymentMethod as { message?: string }}
              >
                <Controller
                  name="paymentMethod"
                  control={control}
                  render={({ field }) => (
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(paymentMethodLabels).map(([value, label]) => {
                        const fieldValue = field.value as string[] | undefined
                        const isChecked = fieldValue?.includes(value) ?? false
                        return (
                          <label
                            key={value}
                            className={`flex items-center gap-2 px-3 py-2 border cursor-pointer transition ${
                              isChecked
                                ? 'border-blue-500 bg-blue-500/10 text-blue-400'
                                : 'border-gray-700 hover:border-blue-500/50'
                            }`}
                          >
                            <input
                              type="checkbox"
                              className="sr-only"
                              checked={isChecked}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  field.onChange([...(fieldValue || []), value])
                                } else {
                                  field.onChange(fieldValue?.filter((v) => v !== value) || [])
                                }
                              }}
                            />
                            <span className="text-sm">{label}</span>
                          </label>
                        )
                      })}
                    </div>
                  )}
                />
              </FormField>
            </section>

            {/* Section 3: Jalons de paiement */}
            <section className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-blue-400">
                  <CreditCard className="w-5 h-5" />
                  <h3 className="text-sm font-mono uppercase tracking-widest">
                    Jalons de paiement
                  </h3>
                </div>
                <button
                  type="button"
                  onClick={() =>
                    appendMilestone({ label: '', percentage: 0, trigger: '', estimatedDate: '' })
                  }
                  className="flex items-center gap-2 px-3 py-1.5 border border-gray-700 text-sm text-gray-300 hover:border-blue-500 hover:text-blue-400 transition"
                >
                  <Plus className="w-4 h-4" />
                  Ajouter
                </button>
              </div>

              {/* Indicateur de pourcentage total */}
              <div
                className={`p-3 border ${
                  Math.abs(totalPercentage - 100) < 0.01
                    ? 'border-green-500/50 bg-green-500/10'
                    : 'border-red-500/50 bg-red-500/10'
                }`}
              >
                <div className="flex items-center gap-2">
                  {Math.abs(totalPercentage - 100) >= 0.01 && (
                    <AlertTriangle className="w-4 h-4 text-red-400" />
                  )}
                  <span className="text-sm">
                    Total des pourcentages:{' '}
                    <span className="font-mono font-semibold">{totalPercentage}%</span>
                    {Math.abs(totalPercentage - 100) >= 0.01 && (
                      <span className="text-red-400 ml-2">(doit être égal à 100%)</span>
                    )}
                  </span>
                </div>
              </div>

              {(errors.milestones as { root?: { message?: string } })?.root && (
                <p className="text-xs text-red-400 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  {(errors.milestones as { root?: { message?: string } }).root?.message}
                </p>
              )}

              <div className="space-y-3">
                {milestoneFields.map((field, index) => (
                  <div
                    key={field.id}
                    className="border border-gray-700 bg-gray-800 p-4 space-y-3 group hover:border-blue-500/50 transition"
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-mono text-gray-400 uppercase tracking-widest">
                        Jalon #{index + 1}
                      </span>
                      {milestoneFields.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeMilestone(index)}
                          className="text-xs text-red-400 hover:text-red-300 transition flex items-center gap-1"
                        >
                          <Trash2 className="w-3 h-3" />
                          Supprimer
                        </button>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                      <div className="md:col-span-2">
                        <FormField
                          label="Libellé"
                          required
                          error={getMilestoneError(index, 'label')}
                        >
                          <input
                            {...register(`milestones.${index}.label`)}
                            className="w-full border border-gray-700 bg-gray-900 p-2 text-sm text-white focus:outline-none focus:border-blue-500 transition"
                            placeholder="Ex: Acompte à la commande"
                          />
                        </FormField>
                      </div>

                      <FormField
                        label="Pourcentage"
                        required
                        error={getMilestoneError(index, 'percentage')}
                      >
                        <div className="relative">
                          <input
                            type="number"
                            {...register(`milestones.${index}.percentage`, {
                              valueAsNumber: true,
                            })}
                            className="w-full border border-gray-700 bg-gray-900 p-2 pr-8 text-sm text-white font-mono focus:outline-none focus:border-blue-500 transition"
                            placeholder="30"
                            min="0"
                            max="100"
                          />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">
                            %
                          </span>
                        </div>
                      </FormField>

                      <FormField
                        label="Date estimée"
                        error={getMilestoneError(index, 'estimatedDate')}
                      >
                        <input
                          type="date"
                          {...register(`milestones.${index}.estimatedDate`)}
                          className="w-full border border-gray-700 bg-gray-900 p-2 text-sm text-white focus:outline-none focus:border-blue-500 transition"
                        />
                      </FormField>
                    </div>

                    <FormField
                      label="Condition / Déclencheur"
                      error={getMilestoneError(index, 'trigger')}
                      hint="Événement qui déclenche le paiement"
                    >
                      <input
                        {...register(`milestones.${index}.trigger`)}
                        className="w-full border border-gray-700 bg-gray-900 p-2 text-sm text-white focus:outline-none focus:border-blue-500 transition"
                        placeholder="Ex: Validation du cahier des charges"
                      />
                    </FormField>
                  </div>
                ))}
              </div>
            </section>

            {/* Section 4: Informations client */}
            <section className="space-y-4">
              <div className="flex items-center gap-2 text-blue-400">
                <Building2 className="w-5 h-5" />
                <h3 className="text-sm font-mono uppercase tracking-widest">
                  Informations client
                </h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField label="Nom / Raison sociale" required error={errors.clientName as { message?: string }}>
                  <input
                    {...register('clientName')}
                    className="w-full border border-gray-700 bg-gray-800 p-2 text-sm text-white focus:outline-none focus:border-blue-500 transition"
                    placeholder="Nom de l'entreprise cliente"
                  />
                </FormField>

                <FormField
                  label="SIREN"
                  error={errors.clientSiren as { message?: string }}
                  tooltip="9 chiffres sans espaces"
                >
                  <input
                    {...register('clientSiren')}
                    className="w-full border border-gray-700 bg-gray-800 p-2 text-sm text-white font-mono focus:outline-none focus:border-blue-500 transition"
                    placeholder="123456789"
                    maxLength={9}
                  />
                </FormField>

                <FormField label="Email" error={errors.clientEmail as { message?: string }}>
                  <input
                    type="email"
                    {...register('clientEmail')}
                    className="w-full border border-gray-700 bg-gray-800 p-2 text-sm text-white focus:outline-none focus:border-blue-500 transition"
                    placeholder="contact@client.fr"
                  />
                </FormField>

                <FormField label="Adresse" error={errors.clientAddress as { message?: string }}>
                  <input
                    {...register('clientAddress')}
                    className="w-full border border-gray-700 bg-gray-800 p-2 text-sm text-white focus:outline-none focus:border-blue-500 transition"
                    placeholder="Adresse postale complète"
                  />
                </FormField>
              </div>
            </section>

            {/* Section 5: Coordonnées bancaires (optionnel) */}
            <section className="space-y-4">
              <div className="flex items-center gap-2 text-gray-400">
                <CreditCard className="w-5 h-5" />
                <h3 className="text-sm font-mono uppercase tracking-widest">
                  Coordonnées bancaires (optionnel)
                </h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <FormField label="IBAN" error={(errors.bankDetails as { iban?: { message?: string } })?.iban}>
                  <input
                    {...register('bankDetails.iban')}
                    className="w-full border border-gray-700 bg-gray-800 p-2 text-sm text-white font-mono focus:outline-none focus:border-blue-500 transition"
                    placeholder="FR76..."
                  />
                </FormField>

                <FormField label="BIC" error={(errors.bankDetails as { bic?: { message?: string } })?.bic}>
                  <input
                    {...register('bankDetails.bic')}
                    className="w-full border border-gray-700 bg-gray-800 p-2 text-sm text-white font-mono focus:outline-none focus:border-blue-500 transition"
                    placeholder="BNPAFRPP"
                  />
                </FormField>

                <FormField label="Nom de la banque" error={(errors.bankDetails as { bankName?: { message?: string } })?.bankName}>
                  <input
                    {...register('bankDetails.bankName')}
                    className="w-full border border-gray-700 bg-gray-800 p-2 text-sm text-white focus:outline-none focus:border-blue-500 transition"
                    placeholder="BNP Paribas"
                  />
                </FormField>
              </div>
            </section>

            {/* Section 6: Options de génération */}
            <section className="space-y-4">
              <h3 className="text-sm font-mono uppercase tracking-widest text-gray-400">
                Options de génération
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <label className="flex items-center gap-3 p-3 border border-gray-700 bg-gray-800 cursor-pointer hover:border-blue-500/50 transition">
                  <input
                    type="checkbox"
                    {...register('includeVatDetails')}
                    className="w-4 h-4 border-gray-700 text-blue-500 focus:ring-blue-500"
                  />
                  <span className="text-sm">Inclure détails TVA</span>
                </label>

                <label className="flex items-center gap-3 p-3 border border-gray-700 bg-gray-800 cursor-pointer hover:border-blue-500/50 transition">
                  <input
                    type="checkbox"
                    {...register('includeLegalMentions')}
                    className="w-4 h-4 border-gray-700 text-blue-500 focus:ring-blue-500"
                  />
                  <span className="text-sm">Mentions légales</span>
                </label>

                <label className="flex items-center gap-3 p-3 border border-gray-700 bg-gray-800 cursor-pointer hover:border-blue-500/50 transition">
                  <input
                    type="checkbox"
                    {...register('includePaymentConditions')}
                    className="w-4 h-4 border-gray-700 text-blue-500 focus:ring-blue-500"
                  />
                  <span className="text-sm">Conditions de paiement</span>
                </label>
              </div>
            </section>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 p-4 border-t border-gray-700 bg-gray-800">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-700 text-gray-300 hover:border-gray-500 transition"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
            >
              {isSubmitting ? 'Génération...' : "Générer l'échéancier"}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
